<template>
  <UIDropdown
    v-bind="$attrs"
    :class="['GptContextMenu', $attrs.class]"
    :items="dropdownOptions"
    :offset="[0, 16]"
    @click.stop.prevent=""
  >
    <template #toggle="{ isVisible }">
      <UIButtonIcon
        data-testid="gpt-card-menu"
        :class="[
          'GptContextMenu__menu',
          { 'GptContextMenu__menu--is-visible': isVisible || isMenuVisible },
        ]"
        icon="dotsHorizontal"
        @click="publicLink.onMenuToggle()"
      />
    </template>

    <template #option="{ option }">
      <UIIcon
        :key="option.icon"
        class="GptContextMenu__option-icon"
        :name="option.icon"
      />
      <span :data-testid="`gpt-menu-${optionTestId(option.label)}`">
        {{ option.label }}
      </span>
    </template>
  </UIDropdown>

  <ModalDeleteGpt
    :isOpen="deleteGptModal.isOpen"
    :gpt="props.gpt"
    :isLoading="isDeleting"
    @close="deleteGptModal.close()"
    @confirm="submitDelete()"
  />

  <ModalGptShare
    :isOpen="shareGptModal.isOpen"
    :isLoading="update.isPending.value"
    :gpt="props.gpt"
    @close="closeShareModal()"
    @confirm="submitShare"
  />

  <ModalGptPublicLink
    v-if="isPlarformAdmin"
    :isOpen="publicLink.modal.isOpen"
    :gpt="props.gpt"
    @close="publicLink.modal.close()"
  />

  <ModalGptRating
    :isOpen="ratingModal.isOpen"
    :isLoading="isLoadingRating"
    @close="ratingModal.close()"
    @confirm="submitRating"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'

import { handleApiErrorToast } from '@/apiClient'
import { useGptQueries } from '@/apiClient/queries/useGptQueries'
import type { IGpt, IGptListItem, IGptPermissions } from '@/apiClient/types/gpt'
import { useGptPublicLinkAction } from '@/composables/useGptPublicLinkAction'
import { useModal } from '@/composables/useModal'
import { useAuthStore } from '@/store/auth'
import { useGptStore } from '@/store/gpt'
import { useToast } from '@/store/toast'
import ModalDeleteGpt from '@/views/components/modals/ModalDeleteGpt.vue'
import ModalGptPublicLink from '@/views/components/modals/ModalGptPublicLink.vue'
import ModalGptRating from '@/views/components/modals/ModalGptRating.vue'
import ModalGptShare from '@/views/components/modals/ModalGptShare.vue'
import UIButtonIcon from '@/views/components/ui/buttons/UIButtonIcon.vue'
import UIDropdown from '@/views/components/ui/dropdowns/UIDropdown.vue'
import type { IIcons } from '@/views/components/ui/icons/types'
import UIIcon from '@/views/components/ui/icons/UIIcon.vue'

defineOptions({
  inheritAttrs: false,
})
const emit = defineEmits(['refresh'])

const props = defineProps<{ gpt: IGptListItem; isMenuVisible?: boolean }>()

const optionTestId = (label: string) =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

const { isPlarformAdmin } = storeToRefs(useAuthStore())

const router = useRouter()
const { update, togglePin, remove, rate } = useGptQueries()

const dropdownOptions = computed<
  { label: string; onClick: () => void; icon: IIcons }[]
>(() => {
  // A draft isn't finalized: only Edit (continue) and Delete (discard) apply —
  // hide chat/pin/share until it's published.
  const isDraft = !!props.gpt.is_draft
  return [
    ...(!isDraft
      ? [
          {
            label: 'New chat',
            onClick: () => {
              router.push({ name: 'NewChat', query: { gptUuid: props.gpt.id } })
            },
            icon: 'newChat' as IIcons,
          },
        ]
      : []),
    ...(props.gpt.is_editable
      ? [
          {
            label: 'Edit',
            onClick: () => {
              router.push({ name: 'GptUpdate', params: { uuid: props.gpt.id } })
            },
            icon: 'pencil' as IIcons,
          },
        ]
      : []),
    ...(!isDraft
      ? [
          {
            label: props.gpt.is_pinned
              ? 'Hide from sidebar'
              : 'Keep in sidebar',
            onClick: () => {
              togglePin
                .mutateAsync(props.gpt.id)
                .then(({ pinned }) => {
                  useToast().onSuccess(
                    `GPT was successfully ${
                      pinned ? 'pinned to' : 'hidden from'
                    } the sidebar`,
                  )
                })
                .catch(handleApiErrorToast)
            },
            icon: (props.gpt.is_pinned ? 'eyeSlash' : 'pin') as IIcons,
          },
        ]
      : []),
    ...(props.gpt.is_editable && !isDraft
      ? [
          {
            label: 'Share',
            onClick: () => {
              shareGptModal.open()
            },
            icon: 'userGroupSolid' as IIcons,
          },
        ]
      : []),
    ...(isPlarformAdmin.value && !isDraft
      ? [
          {
            label: publicLink.label.value,
            onClick: () => {
              publicLink.modal.open()
            },
            icon: 'globe' as IIcons,
          },
        ]
      : []),
    ...(props.gpt.is_ratable
      ? [
          {
            label: 'Rate GPT',
            onClick: () => {
              ratingModal.open()
            },
            icon: 'starRegular' as IIcons,
          },
        ]
      : []),
    ...(props.gpt.is_editable
      ? [
          {
            label: 'Delete',
            onClick: () => {
              deleteGptModal.open()
            },
            icon: 'trash' as IIcons,
          },
        ]
      : []),
  ]
})

const deleteGptModal = useModal<IGpt>()
const isDeleting = ref(false)
const submitDelete = () => {
  if (isDeleting.value) return

  isDeleting.value = true
  remove
    .mutateAsync(props.gpt.id)
    .then(() => {
      useToast().onSuccess('Your GPT was deleted successfully.')
      emit('refresh')
    })
    .finally(() => {
      deleteGptModal.close()
      isDeleting.value = false
    })
}

const shareGptModal = useModal<IGpt>()
const publicLink = useGptPublicLinkAction(
  computed(() => props.gpt.id),
  isPlarformAdmin,
)
// Used to track the share API call
const submitShare = (payload: {
  is_public: boolean
  permissions: IGptPermissions[]
}) => {
  update.mutateAsync({
    ...props.gpt,
    pinned: useGptStore().allGpts.find((gpt) => gpt.id === props.gpt.id)
      ?.pinned,
    ...payload,
  })
}
const closeShareModal = () => {
  shareGptModal.close()
}

// Rating
const ratingModal = useModal<number>()
const isLoadingRating = ref(false)
const submitRating = (rating: number) => {
  if (isLoadingRating.value) return

  isLoadingRating.value = true
  rate
    .mutateAsync({ gptId: props.gpt.id, rating: rating })
    .then(() => {
      useToast().onSuccess('GPT rating was submitted successfully.')
      ratingModal.close()
      emit('refresh')
      useGptStore().refreshGpt(props.gpt.id)
    })
    .catch(handleApiErrorToast)
    .finally(() => {
      isLoadingRating.value = false
    })
}
</script>

<style scoped lang="scss">
:root {
  --gpt-context-menu-icon-size: 1.5rem;
}

.GptContextMenu {
  .GptContextMenu__menu {
    --size: var(--gpt-context-menu-icon-size, 1.5rem);
    display: none;
    --icon-color: #{$color-neutral-100};

    &--is-visible {
      display: block;
    }

    &:hover {
      --icon-color: #{$color-neutral-160};

      &:before {
        opacity: 0;
      }
    }
  }
}

.GptContextMenu__option-icon {
  --color: #{$color-neutral-140};
}
</style>
